// Automatizaciones · lista "Flujos de trabajo creados" + plantillas + estadísticas del módulo.
import type { JSX } from "react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart3, Copy, Pencil, Plus, Sparkles, Trash2 } from "lucide-react";
import { api, ApiError } from "../../../lib/api.ts";
import { useMe } from "../../../lib/auth.ts";
import { formatDate } from "../../../lib/format.ts";
import { Badge, Button, Card, EmptyState, ErrorState, TableSkeleton } from "../../../components/ui.tsx";
import { SearchInput } from "../../../components/SearchInput.tsx";
import { toast } from "../../../components/toast.tsx";
import { ACTION_LABELS, type FlowListItem, LOG_STATUS, Stat, Toggle } from "./shared.tsx";

interface Stats {
  total: number; published: number; active: number; completed: number; pending: number; goalReached: number;
  runs30: number; failed30: number; stopped30: number; actions: Array<{ action: string; status: string; n: number }>;
}

const TEMPLATES = [
  { key: "welcome", name: "Bienvenida a nuevo lead", desc: "Email de bienvenida, puntos y recordatorio a los 2 días." },
  { key: "fb_lead", name: "Lead de Facebook → oportunidad + WhatsApp/email + tarea", desc: "Para formularios de Lead Ads: crea la oportunidad, saluda y asigna una llamada." },
  { key: "form_followup", name: "Seguimiento de formulario", desc: "Etiqueta, emails de seguimiento y tarea si el lead está caliente." },
];

export function FlowList(): JSX.Element {
  const me = useMe();
  const admin = Boolean(me.data?.admin);
  const nav = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [showStats, setShowStats] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  const list = useQuery<{ flows: FlowListItem[] }>({
    queryKey: ["mkt-flows", q],
    queryFn: () => api.get(`/mkt/flows${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`),
  });
  const stats = useQuery<Stats>({ queryKey: ["mkt-flows-stats"], queryFn: () => api.get("/mkt/flows/stats"), enabled: showStats });
  const inval = (): void => { void qc.invalidateQueries({ queryKey: ["mkt-flows"] }); void qc.invalidateQueries({ queryKey: ["mkt-flows-stats"] }); };
  const onErr = (e: unknown): void => toast.error(e instanceof ApiError ? e.message : "No se pudo completar la acción.");

  const toggle = useMutation({
    mutationFn: (v: { id: string; isActive: boolean }) => api.patch(`/mkt/flows/${v.id}/state`, { isActive: v.isActive }),
    onSuccess: inval, onError: onErr,
  });
  const dup = useMutation({
    mutationFn: (id: string) => api.post<{ id: string }>(`/mkt/flows/${id}/duplicate`),
    onSuccess: () => { toast.success("Flujo duplicado (borrador)."); inval(); }, onError: onErr,
  });
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/mkt/flows/${id}`),
    onSuccess: () => { toast.success("Flujo eliminado."); inval(); }, onError: onErr,
  });
  const fromTpl = useMutation({
    mutationFn: (key: string) => api.post<{ id: string }>(`/mkt/flows/templates/${key}`),
    onSuccess: (r) => { inval(); nav(`${r.id}/editar`); }, onError: onErr,
  });

  const flows = list.data?.flows ?? [];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <h2 className="text-lg font-semibold text-ink">Flujos de trabajo creados</h2>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setShowStats((v) => !v)}><BarChart3 size={16} strokeWidth={1.75} /> {showStats ? "Ocultar estadísticas" : "Ver estadísticas del módulo"}</Button>
          {admin && <Button onClick={() => setShowTemplates((v) => !v)}><Sparkles size={16} strokeWidth={1.75} /> Plantillas</Button>}
          {admin && <Button variant="primary" onClick={() => nav("nuevo")}><Plus size={16} strokeWidth={1.75} /> Nuevo flujo</Button>}
        </div>
      </div>

      {showStats && (
        <Card className="p-4 space-y-4">
          {stats.isLoading || !stats.data ? <TableSkeleton rows={2} cols={4} /> : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Flujos" value={stats.data.total} hint={`${stats.data.published} publicados · ${stats.data.active} activos`} />
                <Stat label="Completados" value={stats.data.completed} />
                <Stat label="Pendientes" value={stats.data.pending} />
                <Stat label="Objetivo cumplido" value={stats.data.goalReached} />
              </div>
              <div className="text-sm text-ink-secondary">
                Últimos 30 días: <b>{stats.data.runs30}</b> contactos entraron a flujos · {stats.data.stopped30} detenidos · {stats.data.failed30} con error.
              </div>
              {stats.data.actions.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {stats.data.actions.slice(0, 16).map((a) => (
                    <span key={`${a.action}-${a.status}`} className="text-xs rounded-md border border-line px-2 py-1 text-ink-secondary">
                      {ACTION_LABELS[a.action] ?? a.action} · {LOG_STATUS[a.status]?.label ?? a.status}: <b className="text-ink">{a.n}</b>
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {showTemplates && admin && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {TEMPLATES.map((t) => (
            <Card key={t.key} className="p-4 flex flex-col gap-2">
              <div className="font-medium text-ink">{t.name}</div>
              <p className="text-sm text-ink-muted flex-1">{t.desc}</p>
              <div><Button small onClick={() => fromTpl.mutate(t.key)} disabled={fromTpl.isPending}>Usar plantilla</Button></div>
            </Card>
          ))}
        </div>
      )}

      <Card className="p-0 overflow-hidden">
        <div className="p-3 border-b border-line"><SearchInput value={q} onValueChange={setQ} placeholder="Buscar flujo…" className="max-w-sm" /></div>
        {list.isLoading ? <TableSkeleton rows={5} cols={6} /> : list.isError ? <ErrorState onRetry={() => void list.refetch()} /> : flows.length === 0 ? (
          <EmptyState title="Sin automatizaciones" body="Crea un flujo desde cero o a partir de una plantilla." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-ink-muted border-b border-line">
                <tr>
                  <th className="px-3 py-2">Nombre</th><th className="px-3 py-2">Estado</th><th className="px-3 py-2">Lanzador</th>
                  <th className="px-3 py-2">Creación</th><th className="px-3 py-2">Propietario</th>
                  <th className="px-3 py-2 text-right">Completado</th><th className="px-3 py-2 text-right">Pendiente</th><th className="px-3 py-2 text-right">Cumplido</th>
                  <th className="px-3 py-2">Activa</th><th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {flows.map((f) => (
                  <tr key={f.id} className="border-b border-line last:border-0 hover:bg-surface-hover">
                    <td className="px-3 py-2"><Link to={f.id} className="font-medium text-primary hover:underline">{f.name}</Link></td>
                    <td className="px-3 py-2">{f.status === "published" ? <Badge color="green">Publicado</Badge> : <Badge>Borrador</Badge>}</td>
                    <td className="px-3 py-2 text-ink-secondary">{f.triggerLabel}</td>
                    <td className="px-3 py-2 text-ink-secondary whitespace-nowrap">{formatDate(f.createdAt)}</td>
                    <td className="px-3 py-2 text-ink-secondary">{f.ownerName ?? "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{f.completed}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{f.pending}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{f.goalReached}</td>
                    <td className="px-3 py-2">
                      <Toggle checked={f.isActive} label="Activa" disabled={!admin || toggle.isPending}
                        onChange={(v) => toggle.mutate({ id: f.id, isActive: v })} />
                    </td>
                    <td className="px-3 py-2">
                      {admin && (
                        <div className="flex justify-end gap-1">
                          <button className="btn-ghost btn-sm" title="Editar" onClick={() => nav(`${f.id}/editar`)}><Pencil size={15} strokeWidth={1.75} /></button>
                          <button className="btn-ghost btn-sm" title="Duplicar" onClick={() => dup.mutate(f.id)}><Copy size={15} strokeWidth={1.75} /></button>
                          <button className="btn-ghost btn-sm text-danger" title="Eliminar"
                            onClick={() => { if (window.confirm(`¿Eliminar el flujo "${f.name}" y su historial?`)) del.mutate(f.id); }}>
                            <Trash2 size={15} strokeWidth={1.75} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
